// src/school/pages/exams/ExamList.jsx

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../services/api";
import "./exam.css";

export default function ExamList() {

  const school = JSON.parse(localStorage.getItem("schoolAdmin"));
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [showEdit, setShowEdit] = useState(false);
  const [editExam, setEditExam] = useState(null);

  // =========================
  // LOAD EXAMS
  // =========================
  const loadExams = async () => {
    try {

      setLoading(true);

      const res = await API.get(
        "/school-admin/exams/",
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );

      setExams(res.data.exams || []);

    } catch {
      alert("Failed to load exams ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExams();
  }, []);

  // =========================
  // ARCHIVE
  // =========================
  const archiveExam = async (id) => {

    if (!window.confirm("Archive this exam?")) return;

    try {

      setActionId(id);

      await API.post(
        `/school-admin/exams/archive/${id}/`,
        {},
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );

      loadExams();

    } catch {
      alert("Failed to archive exam ❌");
    } finally {
      setActionId(null);
    }
  };

  // =========================
  // DELETE
  // =========================
  const deleteExam = async (id) => {

    if (!window.confirm("⚠️ This will permanently delete the exam.")) return;

    try {

      setActionId(id);

      await API.delete(
        `/school-admin/exams/delete/${id}/`,
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );

      loadExams();

    } catch {
      alert("Failed to delete exam ❌");
    } finally {
      setActionId(null);
    }
  };

  // =========================
  // OPEN EDIT
  // =========================
  const openEdit = (exam) => {

    setEditExam({ ...exam });

    setShowEdit(true);
  };

  // =========================
  // SAVE EDIT
  // =========================
  const saveEdit = async () => {

    try {

      await API.put(
        `/school-admin/exams/update/${editExam.id}/`,
        {
          name: editExam.name,
          subject: editExam.subject,
          date: editExam.date,
        },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );

      setShowEdit(false);

      loadExams();

    } catch {

      alert("Failed to update exam ❌");

    }

  };

  // =========================
  // SEARCH FILTER
  // =========================
  const filtered = exams.filter(
    (e) =>
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.subject?.toLowerCase().includes(search.toLowerCase())
  );

  // =========================
  // CLASS BADGES
  // =========================
  const renderClasses = (ex) => {

    if (ex.classes?.length) {

      const visible = ex.classes.slice(0, 2);
      const extra = ex.classes.length - visible.length;

      return (
        <div className="badge-wrap">
          {visible.map((c) => (
            <span key={c.id} className="badge">{c.name}</span>
          ))}
          {extra > 0 && <span className="badge more">+{extra}</span>}
        </div>
      );
    }

    return <span className="badge all">All Classes</span>;
  };

  return (

    <div className="exam-container">

      {/* HEADER */}
      <div className="page-header">

        <div>
          <h2 className="page-title">📝 Exams</h2>
          <p className="page-subtitle">
            Manage school exams, subjects & schedules
          </p>
        </div>

        <button
          className="btn-primary"
          onClick={() => navigate("/school-admin/exams/add")}
        >
          + Add Exam
        </button>

      </div>

      {/* SEARCH */}
      <div className="search-box">

        <input
          placeholder="Search by exam name or subject..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

      </div>

      {/* TABLE */}
      <div className="table-wrapper">

        <table className="styled-table">

          <thead>
            <tr>
              <th>Exam</th>
              <th>Subject</th>
              <th>Date</th>
              <th>Class</th>
              <th>Section</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>

          <tbody>

            {loading ? (

              <tr>
                <td colSpan="6" className="no-data">
                  ⏳ Loading exams...
                </td>
              </tr>

            ) : filtered.length === 0 ? (

              <tr>
                <td colSpan="6" className="no-data">
                  No exams found
                </td>
              </tr>

            ) : (

              filtered.map((ex) => (

                <tr key={ex.id}>

                  <td><b>{ex.name}</b></td>
                  <td>{ex.subject}</td>
                  <td>{ex.date}</td>
                  <td>{renderClasses(ex)}</td>
                  <td>{ex.section_name || "All"}</td>

                  <td>

                    <div className="exam-action-row">

                      <button
                        className="exam-btn edit"
                        onClick={() => openEdit(ex)}
                      >
                        Edit
                      </button>

                      <button
                        className="exam-btn archive"
                        disabled={actionId === ex.id}
                        onClick={() => archiveExam(ex.id)}
                      >
                        Archive
                      </button>

                      <button
                        className="exam-btn delete"
                        disabled={actionId === ex.id}
                        onClick={() => deleteExam(ex.id)}
                      >
                        Delete
                      </button>

                    </div>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

      {/* ================= EDIT MODAL ================= */}

      {showEdit && editExam && (

        <div className="modal-backdrop">

          <div className="modal">

            <h3>Edit Exam</h3>

            <input
              value={editExam.name}
              onChange={(e) =>
                setEditExam({ ...editExam, name: e.target.value })
              }
              placeholder="Exam Name"
            />

            <input
              value={editExam.subject}
              onChange={(e) =>
                setEditExam({ ...editExam, subject: e.target.value })
              }
              placeholder="Subject"
            />

            <input
              type="date"
              value={editExam.date}
              onChange={(e) =>
                setEditExam({ ...editExam, date: e.target.value })
              }
            />

            <div className="modal-actions">

              <button
                className="btn-primary"
                onClick={saveEdit}
              >
                Save
              </button>

              <button
                className="btn-secondary"
                onClick={() => setShowEdit(false)}
              >
                Cancel
              </button>

            </div>

          </div>

        </div>

      )}

    </div>

  );
}